import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { AuthImage, AuthAudio } from "@/components/AuthMedia";
import { useLang } from "@/context/LanguageContext";
import { StatusChip, GradeBadge, PaceFlag } from "@/components/Badges";
import { exportCsv } from "@/lib/csv";
import { ArrowLeft, Download } from "lucide-react";

export default function PODetail() {
  const { poId } = useParams();
  const { t } = useLang();
  const nav = useNavigate();
  const [po, setPo] = useState(null);
  const [inspections, setInspections] = useState([]);

  useEffect(() => {
    api.get(`/pos/${poId}`).then((r) => setPo(r.data));
    api.get("/inspections", { params: { po_id: poId } }).then((r) => setInspections(r.data));
  }, [poId]);

  if (!po) return <div>{t("loading")}</div>;

  return (
    <div>
      <button onClick={() => nav(-1)} className="text-sm flex items-center gap-1 mb-3 text-[#4A627A]">
        <ArrowLeft size={16} /> Back
      </button>
      <div className="hag-surface p-5">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <div className="eyebrow">{t("po_number")}</div>
            <h1 className="text-3xl font-bold" style={{ color: "var(--brand-navy)" }}>{po.po_number}</h1>
            <div className="text-sm text-[#4A627A] mt-1">{po.weaver?.name} · {po.product_category}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusChip status={po.status} />
            <PaceFlag flag={po.pace_flag} />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
          <Meta label="ETA" v={po.eta ? new Date(po.eta).toLocaleDateString() : "—"} />
          <Meta label="Inspector" v={po.inspector?.name || "—"} />
          <Meta label="% Complete" v={`${po.percent_complete}%`} />
          <Meta label="Line items" v={(po.line_items || []).length} />
        </div>
      </div>

      <h2 className="mt-6 font-bold" style={{ color: "var(--brand-navy)" }}>Carpet line items</h2>
      <div className="mt-2 overflow-x-auto hag-scroll hag-surface">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF6F0] text-[#4A627A] uppercase text-xs tracking-widest">
            <tr>
              <th className="p-3 text-left">Carpet #</th>
              <th className="p-3 text-left">Design</th>
              <th className="p-3 text-left">Colour</th>
              <th className="p-3 text-left">Size</th>
              <th className="p-3 text-left">Quality</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-left">%</th>
            </tr>
          </thead>
          <tbody>
            {(po.line_items || []).map((ln) => (
              <tr key={ln.id} data-testid={`line-${ln.id}`} className="border-t border-[#1B3A5C]/5">
                <td className="p-3 text-xs">{ln.carpet_number}</td>
                <td className="p-3">{ln.design_code}</td>
                <td className="p-3">{ln.colour}</td>
                <td className="p-3">{ln.size_text}</td>
                <td className="p-3">{ln.quality_spec || "—"}</td>
                <td className="p-3"><StatusChip status={ln.status} /></td>
                <td className="p-3">{ln.percent_complete || 0}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="mt-6 font-bold flex items-center justify-between" style={{ color: "var(--brand-navy)" }}>
        <span>Inspections</span>
        <button
          data-testid="export-inspections-btn"
          onClick={() =>
            exportCsv(
              inspections,
              [
                { key: "date", label: "Date", format: (v) => (v ? new Date(v).toLocaleString() : "") },
                { key: "milestone", label: "Milestone" },
                { key: "percent_complete", label: "% Complete" },
                { key: "grade", label: "Grade" },
                { key: "kpsi", label: "KPSI" },
                { key: "width_inches", label: "Width (in)" },
                { key: "length_woven_cm", label: "Length (cm)" },
                { key: "defects", label: "Defect Count", format: (v) => (v || []).length },
                { key: "photos", label: "Photo Count", format: (v) => (v || []).length },
                { key: "is_audit", label: "Audit?", format: (v) => (v ? "yes" : "") },
              ],
              `${po.po_number}_inspections.csv`
            )
          }
          className="h-9 px-3 rounded-md border border-[#1B3A5C]/15 text-xs flex items-center gap-1 font-normal"
        >
          <Download size={12} /> {t("export_csv")}
        </button>
      </h2>
      <div className="mt-2 space-y-2">
        {inspections.map((ins) => (
          <div key={ins.id} className="hag-surface p-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-sm">{ins.milestone}</span>
              <GradeBadge grade={ins.grade} />
              <span className="text-xs text-[#7C8E9E]">{new Date(ins.date).toLocaleString()}</span>
              <span className="text-xs text-[#7C8E9E]">· {ins.percent_complete}%</span>
              {ins.is_audit && <span className="status-chip pace-amber text-[10px]">AUDIT</span>}
            </div>
            <div className="flex gap-1 mt-2">
              {(ins.photos || []).slice(0, 6).map((p, i) => (
                p.file_id ? <AuthImage key={i} fileId={p.file_id} className="w-14 h-14 rounded object-cover border border-[#1B3A5C]/10" alt="inspection" /> : null
              ))}
            </div>
            {ins.voice_note_file_id && (
              <div className="mt-2">
                <AuthAudio data-testid={`voice-${ins.id}`} fileId={ins.voice_note_file_id} className="w-full" />
                {ins.voice_note_transcript && (
                  <div data-testid={`transcript-${ins.id}`} className="mt-2 p-2 rounded-md text-sm border border-[#1B3A5C]/10 bg-[#FAF6F0]">
                    <div className="text-[10px] uppercase tracking-widest text-[#7C8E9E] flex items-center justify-between">
                      <span>Transcript (HI → text · Whisper)</span>
                      {ins.voice_note_transcribed_at && <span>{new Date(ins.voice_note_transcribed_at).toLocaleString()}</span>}
                    </div>
                    <div className="lang-hi mt-1 leading-relaxed">{ins.voice_note_transcript}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
        {inspections.length === 0 && <div className="text-sm text-[#7C8E9E]">No inspections yet.</div>}
      </div>
    </div>
  );
}

function Meta({ label, v }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-[#7C8E9E]">{label}</div>
      <div className="font-semibold mt-1">{v}</div>
    </div>
  );
}
